import { useState, type FormEvent } from "react";
import Button from "@web-ui/components/Button";
import Field from "@web-ui/components/Field";
import Notice from "@web-ui/components/Notice";
import { session, type Staff } from "@/api/admin";
import type { AdminClient } from "@/api/client";
import { codeProblem, failureMessage, normaliseCode } from "./messages";
import { useCooldown } from "./use-cooldown";

/** Seconds before another code may be asked for (the backend brakes harder behind this). */
export const RESEND_AFTER_SECONDS = 30;

/** The second step: the six digit code from the mail, a new code after a short wait, or another address. */
export function CodeStep({ client, email, onSignedIn, onChangeEmail }: { client: AdminClient; email: string; onSignedIn(staff: Staff): void; onChangeEmail(): void }) {
  const [code, setCode] = useState("");
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [note, setNote] = useState<{ tone: "positive" | "critical"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const cooldown = useCooldown(RESEND_AFTER_SECONDS);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = codeProblem(code);
    setFieldError(problem ?? undefined);
    if (problem) return;
    setBusy(true); setNote(null);
    try { onSignedIn((await session.emailComplete(client, email, normaliseCode(code))).staff); }
    catch (failure) { setNote({ tone: "critical", text: failureMessage(failure) ?? "Sign-in did not work." }); } finally { setBusy(false); }
  };

  const resend = async () => {
    setNote(null); setCode(""); cooldown.restart();
    try { await session.emailStart(client, email); setNote({ tone: "positive", text: "A new code is on its way. The old one no longer works." }); }
    catch (failure) { setNote({ tone: "critical", text: failureMessage(failure) ?? "No new code was sent." }); }
  };

  return (
    <form onSubmit={submit} noValidate>
      {note ? <Notice tone={note.tone} className="mb-6">{note.text}</Notice> : null}
      <p className="text-body-2 text-t-secondary">If <span className="font-semibold break-all text-t-primary">{email}</span> is on the team, a six digit code is on its way. It works for 10 minutes.</p>
      <Field className="mt-6" innerLabel="Six digit code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(event) => setCode(event.target.value)} error={fieldError} required autoFocus />
      <Button className="mt-6 w-full" type="submit" variant="primary" isPending={busy} disabled={busy}>Continue</Button>
      <div className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-2 text-body-2">
        <button type="button" className="font-bold text-t-primary transition-colors hover:text-accent-text disabled:font-normal disabled:text-t-tertiary" disabled={cooldown.left > 0} onClick={() => void resend()}>
          {cooldown.left > 0 ? `Send a new code in ${cooldown.left}s` : "Send a new code"}
        </button>
        <button type="button" className="font-bold text-t-primary transition-colors hover:text-accent-text" onClick={onChangeEmail}>Use another email</button>
      </div>
    </form>
  );
}
